import { FC } from 'react';
import {
    Dialog,
    DialogSurface,
    DialogBody,
    DialogTitle,
    DialogContent,
    DialogActions,
    Text,
} from '@fluentui/react-components';
import { CheckCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SyButton from '../Common/SyButton';
import './SurveyDialog.css';

export interface SurveyActivateDialogProps {
    open: boolean;
    surveyName?: string;
    onClose: () => void;
    onConfirm: () => void;
    isActivating?: boolean;
}

const SurveyActivateDialog: FC<SurveyActivateDialogProps> = ({
    open,
    surveyName,
    onClose,
    onConfirm,
    isActivating = false,
}) => {
    const { t } = useTranslation();

    return (
        <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
            <DialogSurface className="survey-dialog-surface">
                <DialogBody className="survey-dialog-body">
                    <DialogTitle className="survey-dialog-title">
                        <CheckCheck size={24} className="survey-dialog-icon-green" />
                        <Text className="survey-dialog-title-text">
                            {t('adminSettings.surveys.activateDialog.title', 'Activating the survey?')}
                        </Text>
                    </DialogTitle>
                    <DialogContent className="survey-dialog-content">
                        <Text className="survey-dialog-message-text">
                            {t(
                                'adminSettings.surveys.activateDialog.message',
                                'Are you sure you want to activate the survey'
                            )}
                            {surveyName && (
                                <>
                                    {' '}
                                    <span className="survey-dialog-survey-name">" {surveyName}"</span>
                                </>
                            )}
                            ?
                        </Text>
                        <div>
                            <Text className="survey-dialog-warning-text">
                                {t(
                                    'adminSettings.surveys.activateDialog.warning',
                                    'All users will have to answer the survey next time they use SyGPT.'
                                ).split('\n').map((line, i, arr) => (
                                    <span key={i}>
                                        {line}
                                        {i < arr.length - 1 && <br />}
                                    </span>
                                ))}
                            </Text>
                        </div>
                    </DialogContent>
                    <DialogActions className="survey-dialog-actions">
                        <SyButton
                            appearance="secondary"
                            onClick={onClose}
                            disabled={isActivating}
                        >
                            {t('cancel', 'Cancel')}
                        </SyButton>
                        <SyButton
                            appearance="primary"
                            onClick={onConfirm}
                            disabled={isActivating}
                            icon={<CheckCheck size={16} />}
                        >
                            {isActivating
                                ? t('adminSettings.surveys.activating', 'Activating...')
                                : t('adminSettings.surveys.activate', 'Activate survey')}
                        </SyButton>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default SurveyActivateDialog;
